import React, { useMemo } from "react";

import { ActivityFormProps, Step } from "./types";
import { isFormComplete, validateStep } from "./utils/ValidationUtil";

import { Step1 } from "./steps/Step1";
import { Step2 } from "./steps/Step2";
import { Step3 } from "./steps/Step3";
import { Step4 } from "./steps/Step4";
import { Step5 } from "./steps/Step5";
import { StepErrorAlert } from "./activity-components/StepErrorAlert";
import { StepNavigation } from "./activity-components/StepNavigation";
import { FormNavigationButtons } from "./activity-components/FormNavigationButtons";
import { useActivityFormState } from "./hooks/useActivityFormState";
import { useActivityFormHandlers } from "./hooks/useActivityFormHandlers";

// ── Konstanter ────────────────────────────────────────────────────────────────

const FIELD_LABELS: Record<string, string> = {
  startAcceptCriteria: "Godkänn villkoren",
  certificationStatus: "Självcertifiering",
  name: "Namn på aktivitetsarrangör",
  description: "Beskrivning av aktivitet",
  types: "Aktivitetstyper",
  ages: "Ålder",
  municipalities: "Kommuner",
  orgNumber: "Organisationsnummer",
  email: "E-postadress",
  address: "Adress",
  addresses: "Adress",
  contactEmail: "Kontakt-e-post",
  newsletterOptIn: "Samtycke till nyhetsbrev",
  agreePublish: "Samtycke till publicering av uppgifter",
};

const STEP_LABELS: Record<Step, string> = {
  1: "Start",
  2: "Om aktiviteten",
  3: "Om oss",
  4: "Kontakt",
  5: "Bekräfta",
};

// ── Komponent ─────────────────────────────────────────────────────────────────

export const ActivityForm: React.FC<ActivityFormProps> = (props) => {
  // ── Form State
  const {
    data,
    setData,
    step,
    setStep,
    submitted,
    setSubmitted,
    isSubmitting,
    setIsSubmitting,
    errors,
    setErrors,
    stepRef,
    thankYouRef,
    errorAlertRef,
    INITIAL_ACTIVITY,
  } = useActivityFormState(props.submissionPage);

  // ── Form Handlers
  const {
    onChange,
    onMultiChange,
    clearError,
    onBlur,
    next,
    prev,
    submit,
    createNewActivity,
  } = useActivityFormHandlers({
    step,
    data,
    setData,
    setStep,
    setErrors,
    setSubmitted,
    setIsSubmitting,
    errorAlertRef,
    postUrl: props.postUrl,
    initialActivity: INITIAL_ACTIVITY,
  });

  // ── Derived state
  const canProceed = useMemo(
    () => validateStep(step, data).size === 0,
    [step, data],
  );
  const formComplete = useMemo(() => isFormComplete(data), [data]);

  // ── Shared props for step components
  const sharedProps = {
    data,
    setData,
    validationErrors: errors,
    clearValidationError: clearError,
    stepFocusRef: stepRef,
    errorAlertRef,
    onBlur
  };

  // ── Thank you page
  if (submitted) {
    return (
      <div className="container p-0">
        <div>
          <div
            ref={thankYouRef}
            tabIndex={-1}
            aria-label="Tack, din aktivitet har skickats in."
          />
          {props.thankYouHtml && (
            <div dangerouslySetInnerHTML={{ __html: props.thankYouHtml }} />
          )}
          <button
            type="button"
            className="mt-5 button-primary"
            onClick={createNewActivity}
          >
            Skapa ny aktivitet
          </button>
        </div>
      </div>
    );
  }

  // ── Main form
  return (
    <div className="grid container">
      <form
        className="grid g-col-12 g-col-lg-8 vgr-form activity"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        aria-busy={isSubmitting}
      >
        {/* ── Step Navigation ── */}
        <StepNavigation
          currentStep={step}
          onStepClick={setStep}
          stepLabels={STEP_LABELS}
        />

        {/* ── Error Alert ── */}
        {errors?.size > 0 && (
          <div ref={errorAlertRef} className="g-col-8">
            <StepErrorAlert
              errors={Object.fromEntries(
                Array.from(errors).map((key) => [
                  key,
                  { label: FIELD_LABELS[key] },
                ]),
              )}
              alertIconSvg={props.alertIconSvg}
            />
          </div>
        )}

        {/* ── Step Content ── */}
        {step === 1 && <Step1 {...sharedProps} {...props} />}
        {step === 2 && (
          <Step2
            {...sharedProps}
            descriptionOfTab2={props.descriptionOfTab2}
            activityTypes={props.activityTypes}
            ageGroups={props.ageGroups}
            municipalities={props.municipalities}
            onChange={onChange}
            onMultiChange={onMultiChange}
            svgSearch={props.svgSearch}
            descriptionOfAddressField={props.descriptionOfAddressField}
          />
        )}
        {step === 3 && (
          <Step3
            {...sharedProps}
            descriptionOfTab3={props.descriptionOfTab3}
            onChange={onChange}
          />
        )}
        {step === 4 && (
          <Step4
            {...sharedProps}
            descriptionOfTab4={props.descriptionOfTab4}
            newsletterOptInText={props.newsletterOptInText}
            agreePublishText={props.agreePublishText}
            onChange={onChange}
          />
        )}
        {step === 5 && <Step5 data={data} stepFocusRef={stepRef} {...props} />}

        {/* ── Navigation Buttons ── */}
        <FormNavigationButtons
          currentStep={step}
          canProceed={canProceed}
          formComplete={formComplete}
          isSubmitting={isSubmitting}
          onPrev={prev}
          onNext={next}
          onSubmit={submit}
          hasErrors={errors.size > 0}
        />
      </form>
    </div>
  );
};
